import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import {
  verificationPhotoApi,
  type VerificationPhoto,
} from '../../services/verificationPhotoService';

export interface VerificationPhotoThumbnailsProps {
  pharmacyId: string;
  orderId: string;
  photos: VerificationPhoto[];
  /** Removal is offered only while the item is unsigned; the API still enforces admin-only. */
  onRemove?: (photo: VerificationPhoto) => void;
  onOpen?: (photo: VerificationPhoto, objectUrl: string) => void;
}

/** "photo by Ada Ops at 3:12 PM" — the same sentence the timeline and the hardcopy use. */
export function describeCapture(photo: VerificationPhoto): string {
  const who = photo.capturedByName || photo.capturedByUserId || 'unknown user';
  const when = photo.capturedAt ? format(new Date(photo.capturedAt), 'MMM dd, yyyy h:mm a') : 'unknown time';
  const unit = photo.scope === 'pack' ? 'whole pack' : `Rx ${photo.rxNumber ?? ''}`.trim();
  return `${unit} — photo by ${who} at ${when}`;
}

/**
 * PRX-51 — the captured evidence for one item.
 *
 * The image is fetched with the JWT and turned into an object URL, the way
 * DrugImageThumbnail already does: there is no signed URL to put in a src, on
 * purpose (see the controller docstring).
 */
export const VerificationPhotoThumbnails: React.FC<VerificationPhotoThumbnailsProps> = ({
  pharmacyId,
  orderId,
  photos,
  onRemove,
  onOpen,
}) => {
  // id -> object URL, or null for "this one failed". The null is a sentinel, not
  // an absence: the loader skips any id it has already answered, so a photo whose
  // bytes cannot be fetched is not re-requested on every list change.
  const [urls, setUrls] = useState<Record<string, string | null>>({});
  // Every object URL this strip has created, revoked once on unmount. Revoking
  // in the loader's cleanup instead would kill the URLs of the tiles ALREADY on
  // screen the moment a second photo lands and the effect re-runs.
  const createdRef = useRef<string[]>([]);

  useEffect(
    () => () => {
      createdRef.current.forEach((url) => URL.revokeObjectURL(url));
      createdRef.current = [];
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      for (const photo of photos) {
        if (photo.id in urls) continue;
        try {
          const blob = await verificationPhotoApi.fetchImage(pharmacyId, orderId, photo.id);
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          createdRef.current.push(url);
          setUrls((prev) => ({ ...prev, [photo.id]: url }));
        } catch {
          // Say so on the tile rather than showing a broken image. The evidence
          // row still exists and the item stays satisfied — it is the preview
          // that failed, not the check.
          if (!cancelled) setUrls((prev) => ({ ...prev, [photo.id]: null }));
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyId, orderId, photos.map((p) => p.id).join(',')]);

  if (photos.length === 0) return null;

  return (
    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
      {photos.map((photo) => {
        const url = urls[photo.id];
        // Three states, and they must not be conflated: a string is the loaded
        // image, null is a fetch that failed, undefined is a fetch still in
        // flight. Treating "still loading" as "failed" would tell the operator
        // the evidence is broken while it is on its way.
        const loading = url === undefined;
        return (
        <Tooltip key={photo.id} title={describeCapture(photo)}>
          <Box sx={{ position: 'relative', width: 72, height: 72 }}>
            {url ? (
              <Box
                component="img"
                src={url}
                alt={describeCapture(photo)}
                onClick={() => onOpen?.(photo, url)}
                sx={{
                  width: 72,
                  height: 72,
                  objectFit: 'cover',
                  borderRadius: 1,
                  cursor: onOpen ? 'pointer' : 'default',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.5,
                }}
              >
                {loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <Typography variant="caption" color="text.secondary" align="center">
                    Photo could not be loaded
                  </Typography>
                )}
              </Box>
            )}
            {onRemove && (
              <IconButton
                size="small"
                aria-label={`Remove photo for ${photo.scope === 'pack' ? 'the whole pack' : `Rx ${photo.rxNumber ?? ''}`}`}
                onClick={() => onRemove(photo)}
                sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'background.paper' }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            )}
          </Box>
        </Tooltip>
        );
      })}
    </Stack>
  );
};

export default VerificationPhotoThumbnails;
